import * as styles from "./ClosureWeeksBody.css";

import { useAtomValue, useSetAtom } from "jotai";
import type { JSX, ReactNode } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { NumberField } from "@frontend/components/atoms/NumberField";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { Plural, Trans, useLingui } from "@lingui/react/macro";

import {
  closureWeeksAtom,
  impactedAmendmentsAtom,
  overrunAtom,
  prefillClosureWeeksAtom,
  prefilledValidatedAtom,
  remainingAtom,
  selectedCountAtom,
} from "../stores/closureWeeks";
import { fullDate, weekSpan } from "../utils/dates";
import { ClosureWeekGrid } from "./ClosureWeekGrid";

export type ClosureWeeksBodyProps = {
  readOnly?: boolean;
  yearField?: ReactNode;
};

export function ClosureWeeksBody({
  readOnly = false,
  yearField,
}: ClosureWeeksBodyProps): JSX.Element {
  const { t } = useLingui();

  const { year, contractClosureWeeksCount, loading, error, blockingWeeks } =
    useAtomValue(closureWeeksAtom);
  const selectedCount = useAtomValue(selectedCountAtom);
  const remaining = useAtomValue(remainingAtom);
  const overrun = useAtomValue(overrunAtom);
  const impacted = useAtomValue(impactedAmendmentsAtom);
  const prefilledValidated = useAtomValue(prefilledValidatedAtom);

  const prefill = useSetAtom(prefillClosureWeeksAtom);

  const previousYear = year - 1;

  return (
    <div className={styles.body}>
      {error ? (
        <div className={styles.alert} role="alert">
          <p className={styles.alertHeadline}>{error.split("\n")[0]}</p>

          {blockingWeeks.map((blocking) => {
            const week = blocking.week;
            const start = fullDate(blocking.start);
            const end = fullDate(blocking.end);
            const hourCount = blocking.hourCount;

            return (
              <p key={week} className={styles.alertWeek}>
                <strong>
                  <Trans>
                    Week {week} ({start} – {end}) — {hourCount} validated
                    hour(s):
                  </Trans>
                </strong>{" "}
                {blocking.employees
                  .map((employee) => `${employee.name} (${employee.hourCount})`)
                  .join(", ")}
              </p>
            );
          })}
        </div>
      ) : null}

      <div className={styles.header}>
        <div className={styles.controls}>
          {yearField}

          <Field orientation="horizontal" className={styles.weeksOwed}>
            <FieldLabel>
              <Trans>Weeks owed by the contract</Trans>
            </FieldLabel>

            <FieldContent>
              <NumberField value={contractClosureWeeksCount} disabled />
            </FieldContent>
          </Field>
        </div>

        <div className={styles.prefills} hidden={readOnly}>
          <Button
            variant="neutral"
            size="sm"
            disabled={loading}
            onClick={() => void prefill("holidays")}
          >
            <Trans>From school holidays</Trans>
          </Button>

          <Button
            variant="neutral"
            size="sm"
            disabled={loading}
            onClick={() => void prefill("previous")}
          >
            <Trans>From {previousYear}</Trans>
          </Button>
        </div>
      </div>

      <div className={styles.tallies}>
        <p className={styles.tally}>
          <Plural
            value={selectedCount}
            _0="No week closed"
            one="# week closed"
            other="# weeks closed"
          />

          {!overrun && 0 < remaining ? (
            <>
              {" · "}
              <Plural
                value={remaining}
                one="# still to place"
                other="# still to place"
              />
            </>
          ) : null}
        </p>

        {null === contractClosureWeeksCount ? (
          <p className={styles.tally}>
            <Trans>
              The contract in force sets no worked weeks: the subscription owes
              no closure this year.
            </Trans>
          </p>
        ) : null}
      </div>

      {0 === contractClosureWeeksCount ? (
        <p className={styles.alert} role="alert">
          <Trans>
            The contract in force works every week of {year}: it leaves no
            closure week, and the amendments of its collaborateurs cannot be
            generated. Check the worked weeks the contract sets.
          </Trans>
        </p>
      ) : null}

      {0 < prefilledValidated.length ? (
        <div className={styles.notice}>
          <Plural
            value={prefilledValidated.length}
            one="The proposal ticked a week whose hours are already validated: for the collaborateurs concerned it stays informative and changes none of their hours."
            other="The proposal ticked # weeks whose hours are already validated: for the collaborateurs concerned they stay informative and change none of their hours."
          />

          <ul className={styles.employees}>
            {prefilledValidated.map((week) => (
              <li key={week.number}>
                {t`Week ${week.number}`} ({weekSpan(week.start, week.end)})
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {overrun ? (
        <p className={styles.alert} role="alert">
          <Trans>
            More weeks are closed than the {contractClosureWeeksCount} owed for
            the year: saving is refused until one is unticked.
          </Trans>
        </p>
      ) : null}

      {0 < impacted.length ? (
        <div className={styles.notice}>
          <Plural
            value={impacted.length}
            one="An amendment was generated with other weeks; saving flags it for review."
            other="# amendments were generated with other weeks; saving flags them for review."
          />

          <ul className={styles.employees}>
            {impacted.map((amendment) => (
              <li key={amendment.amendmentId}>
                {amendment.employeeName ?? t`Unnamed employee`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {loading ? (
        <div className={styles.loading}>
          <Spinner aria-label={t`Loading`} />
        </div>
      ) : (
        <ClosureWeekGrid readOnly={readOnly} />
      )}
    </div>
  );
}
